import {
  CreateButton,
  EditButton,
  FunctionField,
  ShowButton,
  TextField,
  TopToolbar,
  WrapperField,
  TextInput,
} from 'react-admin';

import CraftList from 'src/components/CraftList';
import AuditBasedStatusFilterInput from 'src/inputs/AuditBasedStatusFilterInput';
import IntegerInput from 'src/inputs/IntegerInput';
import ClearFiltersButton from 'src/components/ClearFiltersButton';
import BulkSoftDeleteButton from 'src/components/BulkSoftDeleteButton';
import { usePermissions } from 'src/hooks';
import { CriteriaGroupRecord } from 'src/types';
import TruncatedTextWithTooltipField from 'src/fields/TruncatedTextWithTooltipField';

const columnStyles = {
  actions: {
    width: '12rem',
    '& :nth-child(n + 2)': {
      ml: 0.5,
    },
  },
};

const filters = [
  <AuditBasedStatusFilterInput key="statusFilter" source="archived_at@_is_null" inactiveLabel="Archived" alwaysOn />,
  <IntegerInput
    key="orgIdFilter"
    label="Organization ID"
    source="organizations_criteria_groups.organization.id"
    alwaysOn
  />,
  <TextInput
    key="orgNameFilter"
    label="Organization Name"
    source="organizations_criteria_groups.organization.name@_ilike"
    alwaysOn
  />,
];

const ListActions = ({ canCreate }: { canCreate: boolean }) => (
  <TopToolbar>
    <ClearFiltersButton />
    {canCreate && <CreateButton />}
  </TopToolbar>
);

export const CriteriaGroupList = () => {
  const { permissions } = usePermissions();

  const canCreate = permissions.has('criteriaGroup:edit');
  const canEdit = permissions.has('criteriaGroup:edit');
  const canDelete = permissions.has('criteriaGroup:delete');

  const bulkActionButtons = canDelete && (
    <BulkSoftDeleteButton
      label="Delete Selected"
      dialogTitle="Delete Risk Scans (%{smart_count})"
      dialogContent="Are you sure you want to delete these items?"
      data={{ archived_at: 'now()' }}
    />
  );

  return (
    <CraftList
      title="Risk Scans"
      filters={filters}
      filterDefaultValues={{ 'archived_at@_is_null': true }}
      actions={<ListActions canCreate={canCreate} />}
      showClearButton={false}
      sort={{ field: 'name', order: 'ASC' }}
      dataGridProps={{ bulkActionButtons }}
      columnStyles={columnStyles}
    >
      <TextField source="name" />
      <FunctionField
        label="Organization IDs"
        sortable={false}
        render={(record: CriteriaGroupRecord) => {
          const orgIds = record.organizations_criteria_groups?.map((og) => og.organization.id).join(', ') || '-';
          return <TruncatedTextWithTooltipField text={orgIds} maxWidth={200} />;
        }}
      />
      <FunctionField
        label="Organization Names"
        sortable={false}
        render={(record: CriteriaGroupRecord) => {
          const orgNames = record.organizations_criteria_groups?.map((og) => og.organization.name).join(', ') || '-';
          return <TruncatedTextWithTooltipField text={orgNames} maxWidth={200} />;
        }}
      />
      <WrapperField label="Actions" headerClassName="column-actions" cellClassName="column-actions">
        <ShowButton />
        {canEdit && (
          <FunctionField render={(rec: CriteriaGroupRecord) => <EditButton disabled={!!rec.archived_at} />} />
        )}
      </WrapperField>
    </CraftList>
  );
};

export default CriteriaGroupList;
